import { type CreateAppRequest } from '@studio/shared';
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { Failure } from '../../shell/EmptyState.js';
import { useNavigate, type Surface } from '../../shell/router.js';
import { appRequestFor } from '../../workspace/order.js';

/**
 * Adding an App, with both ways in on one screen.
 *
 * A pasted listing address or package name is something the Studio can go and read; a
 * name on its own is an App that has nothing on Play yet, and publication is never a
 * prerequisite for creative exploration. The same one field decides which of the two
 * this is, so there is no mode to pick and no setup to finish.
 */
function AddAppPage(): ReactNode {
  const [paste, setPaste] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const create = useCallback(
    async (request: CreateAppRequest) => {
      setBusy(true);
      setError(null);
      try {
        const created = await api.createApp(request);
        navigate(`/apps/${created.app.id}`);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Не удалось добавить App.');
      } finally {
        setBusy(false);
      }
    },
    [navigate],
  );

  return (
    <section className="page">
      <p className="eyebrow">Добавить App</p>
      <h1>Добавьте App.</h1>
      <p className="note">
        Вставьте то, что уже есть. Адрес Google Play или имя пакета сканируется по всем странам в
        Inventory; всё остальное — просто имя App, которое ещё не опубликовано.
      </p>

      {error === null ? null : <Failure message={error} />}

      <form
        className="add-app"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void create(appRequestFor(paste));
        }}
      >
        <label htmlFor="new-app-paste">Адрес Google Play или имя пакета</label>
        <input
          id="new-app-paste"
          value={paste}
          onChange={(event) => setPaste(event.target.value)}
          placeholder="play.google.com/store/apps/details?id=…"
        />
        <button className="primary" type="submit" disabled={busy || paste.trim() === ''}>
          {busy ? 'Добавляю…' : 'Добавить и просканировать этот listing'}
        </button>
      </form>

      <form
        className="add-app"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          void create({ displayName: name.trim() });
        }}
      >
        <label htmlFor="new-app-name">Имя App, которое ещё не опубликовано</label>
        <input id="new-app-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Aurora Browser" />
        <button className="secondary" type="submit" disabled={busy || name.trim() === ''}>
          Создать неопубликованный App
        </button>
      </form>
    </section>
  );
}

export const surface: Surface = {
  id: 'app-new',
  path: '/apps/new',
  title: 'Добавить App',
  render: () => <AddAppPage />,
};
